// [uTheory](http://utheory.com) - Copyright (c) Gregory Ristow 2015
//
// ## Blank for Interactive Game
//
// Include description of object & useful info here, please!

import InteractivePrototype from './interactivePrototype.ts';
import utils from '../utils.ts';
import QuestionGenerator from '../uTheory.Skills/questionGenerator';
import { Note, Interval } from '../notation.ts';

class EnharmonicIntervals extends InteractivePrototype {
  constructor(displayDiv, options) {
    super();
    this.targetSpeedFromSkills = EnharmonicIntervals.registry.skills;
    this.init(displayDiv, options);
  }

  init(displayDiv, options) {
    this.settings = {
      instructions: {
        ariaLive: 'alert',
        ariaLiveRegion: 0,
      },
      letters: {
        numberButtons: true,
        intervalQualityButtons: true,
        checkButton: true,
      },
      checkMark: {},
      player: {},
      progressTracker: true,
    };

    options.maxQuestions = options.maxQuestions || 5;

    super.init(displayDiv, options);
  }

  generateQuestions(questions) {
    if (this.options.drill) {
      this.questions = this.generateDrillQuestions();
    } else {
      this.questions = this.generateManualQuestions(questions);
    }

    this.questionPrompt = 'What is an interval enharmonically equivalent to {question}?';

    if (!this.options.testMode) {
      this.questionPrompt += `<br /><small>Question {question} of ${this.questions.length}</small>`;
    }

    this.questionPrompt += "<br /><span id='enharmonicIntervalsAnswerDiv'>_</span>";

    this.answer = {};
  }

  generateDrillQuestions() {
    const qg = new QuestionGenerator();
    return qg
      .interval_inversion(this.options.maxQuestions)
      .map((question) => question.interval)
      .map(this.processQuestion);
  }

  generateManualQuestions(questions) {
    questions = questions || this.options.questions;
    return questions.map((question) => new Interval(question)).map(this.processQuestion, this);
  }

  processQuestion(interval, i) {
    let questionText = interval.longName();
    questionText = `${utils.aOrAn(questionText)} ${questionText}`;
    return {
      interval,
      text: [questionText, i + 1],
      skills: 'enharmonic_intervals',
    };
  }

  addListeners() {
    this.letterPanel.addNumberCallback(this.addIntervalNameToAnswer, this);
    this.letterPanel.addIntervalQualityCallback(this.addIntervalQualityToAnswer, this);
    this.letterPanel.addCheckCallback(this.checkAnswer, this);
  }

  addIntervalNameToAnswer(generic) {
    this.answer.intervalName = generic;

    return this.displayAnswer();
  }

  addIntervalQualityToAnswer(quality) {
    this.answer.intervalQuality = quality;

    return this.displayAnswer();
  }

  displayAnswer() {
    const answer = this.answer.intervalQuality + this.answer.intervalName;
    const answerDiv = document.getElementById('enharmonicIntervalsAnswerDiv');

    if (answer === '') {
      answerDiv.removeAttribute('aria-live');
      answerDiv.innerHTML = '_';
    } else {
      answerDiv.setAttribute('aria-live', 'polite');
      answerDiv.innerHTML = answer;
    }
  }

  playAnswer() {
    const C = new Note();
    const note2 = C.getNoteAt(this.currentQuestion.interval);
    this.player.play(C, { startTime: 0, stopTime: 1.5 });
    this.player.play(note2, { startTime: 0.5, stopTime: 1.5 });
  }

  newQuestion() {
    this.answer.intervalQuality = '';
    this.answer.intervalName = '';
    super.newQuestion();
  }

  displayInstructions() {
    super.displayInstructions();
    this.displayAnswer();
  }

  checkAnswer() {
    if (this.rightAnswerDelay) return undefined;
    if (!this.answer.intervalQuality) {
      return this.checkMark.warn('You must specify an interval quality.');
    }
    if (!this.answer.intervalName) {
      return this.checkMark.warn('You must specify an interval number.');
    }

    let answerInterval;
    let answerString;
    try {
      answerInterval = new Interval(this.answer.intervalQuality + this.answer.intervalName);
    } catch (intervalError) {
      answerInterval = false;
      answerString = this.answer.intervalQuality + this.answer.intervalName;
    }

    const correct =
      answerInterval && answerInterval.equalsEnharmonically(this.currentQuestion.interval);

    if (this.options.testMode) {
      return this.checkpointAnswer({
        interval: answerInterval,
        intervalString: answerString,
        correct,
      });
    }

    if (correct) {
      this.rightAnswer(1000);
    } else {
      this.wrongAnswer();
    }
    return undefined;
  }

  // ## CheckPoint Mode Questions:
  newCheckpointQuestion(questionString) {
    return this.loadingPromise.then(() => {
      this.generateQuestions([questionString]);
      return Promise.resolve(this.newQuestion());
    });
  }

  // For checkpoint tests, the answer & data from the current question is
  // logged via a callback.
  getAnswerAndData(answer) {
    const answerInterval = answer
      ? answer.interval
        ? answer.interval.longName()
        : answer.intervalString
        ? answer.intervalString
        : ''
      : '';
    answer = answer || {};
    const questionText = `What is an interval enharmonically equivalent to ${this.currentQuestion.text[0]}`;
    const answerAndData = {
      question: this.currentQuestion.interval.longName(),
      questionType: 'EnharmonicIntervals',
      questionText,
      questionSVG: null,
      studentAnswerText: answerInterval,
      studentAnswerSVG: null,
      correctAnswerText: this.currentQuestion.interval
        .getEnharmonicallyEquivalentInterval()
        .longName(),
      rightAnswers: answer.correct ? 1 : 0,
      wrongAnswers: answer.correct ? 0 : 1,
      skills: this.currentQuestion.skills,
    };
    return answerAndData;
  }
}

EnharmonicIntervals.registry = {
  description: 'Enharmonic Intervals',
  skills: 'enharmonic_intervals',
};

export default EnharmonicIntervals;
